/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import React, {
  ChangeEvent, FormEvent, useEffect, useMemo, useState
} from 'react';
import { useLocation } from 'react-router';
import {
  IlButton,
  IlInlineLoading,
  IlTextInput,
  IlInlineNotification,
  IlSelect,
  IlSelectItem
} from 'il-framework-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import usePreserveSearchNav from '../../../../app/shared/hooks/usePreserveSearchNav';
import {
  DEFAULT_SMS_DIGITS,
  FORBIDDEN_CHARACTERS_REGEX,
  I18nNamespaces,
  LABEL_MAX_LENGTH,
  LOA_RESEND_SECONDS,
  ValidationErrorKey as ValErr
} from '../../../../app/shared/utils/constants.util';
import { RootState } from '../../../../app/store';
import { AuthErrorKey, SecuritySetupType } from '../../../../domain/models/impl/SecuritySetup';
import { getLoaValidationMessageKey, useRequiredLevel } from '../../../../app/shared/utils/helpers.util';
import {
  setError,
  setValidationError,
  verifyOrActivateFactor,
  challengeFactor,
  enrollSMSFactor,
  resetEnrollmentState
} from '../../setupSecuritySlice';
import countries from '../../../../app/shared/utils/country-list.json';
import styles from './EnrollSMS.scss';

type Country = { name: string; dial_code: string; code: string };

const getDialCode = (iso: string, list: Country[]): string => {
  if (iso === 'Other') return '';
  return list.find((c) => c.code === iso)?.dial_code ?? '';
};

const EnrollSMS: React.FC = () => {
  const { t } = useTranslation([I18nNamespaces.LOA_ENRICHMENT, I18nNamespaces.COUNTRIES_NAMES]);
  const dispatch = useDispatch();
  const requiredLevel = useRequiredLevel();
  const { replaceQueryParams } = usePreserveSearchNav();
  const actionLoading = useSelector((s: RootState) => s.setupSecurity.actionLoading);
  const validationError = useSelector((s: RootState) => s.setupSecurity.validationError);
  const smsErrorKey = useSelector((s: RootState) => s.setupSecurity.error);
  const selectedFactor = useSelector((s: RootState) => s.setupSecurity.selectedFactor);

  const [label, setLabel] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState<number>(0);

  const factorId = selectedFactor?.factorId ?? '';
  const digits = selectedFactor?.digits ?? DEFAULT_SMS_DIGITS;
  const phone = (selectedFactor?.data ?? '') as string;

  const typedDigits = code.replace(/\D/g, '').length;
  const shouldShowInlineNotification = !!smsErrorKey;

  const { search } = useLocation();
  const smsFactorIdFromUrl = useMemo(() => {
    const p = new URLSearchParams(search || '');
    return p.get('smsFactorId') ?? '';
  }, [search]);

  const isLabelError = validationError?.startsWith('label');
  const isPhoneNumberError = !!validationError && !isLabelError;

  const step: 'details' | 'code' = smsFactorIdFromUrl ? 'code' : 'details';

  const parsedPhone = useMemo(() => {
    const raw = phoneNumber.trim();
    if (!raw) return null;

    try {
      if (countryCode === 'Other') {
        if (!raw.startsWith('+')) return null;
        return parsePhoneNumberWithError(raw);
      }

      return parsePhoneNumberWithError(raw, countryCode as CountryCode);
    } catch {
      return null;
    }
  }, [phoneNumber, countryCode]);

  const dialCode = useMemo(() => {
    if (countryCode === 'Other') {
      const cc = parsedPhone?.countryCallingCode;
      return cc ? `+${cc}` : '';
    }

    return getDialCode(countryCode, countries as Country[]);
  }, [countryCode, parsedPhone]);

  const isPhoneValid = Boolean(parsedPhone?.isValid());
  const nationalNumber = parsedPhone ? String(parsedPhone.nationalNumber) : '';

  const canNext = Boolean(label.trim())
    && Boolean(dialCode)
    && isPhoneValid
    && !actionLoading;

  const canResend = Boolean(factorId) && resendIn === 0 && !actionLoading;

  const canSubmit = Boolean(factorId)
    && digits > 0
    && !actionLoading
    && !validationError
    && typedDigits === digits;

  const inlineTitleMapAddPhone: Record<string, string> = {
    [AuthErrorKey.ResourceAlreadyExists]: t(
      'loaEnrichment.factors.sms.inline.addPhoneAlreadyExists',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    ),
    [AuthErrorKey.ResourceForbidden]: t(
      'loaEnrichment.factors.sms.inline.addPhoneForbidden',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    ),
    [AuthErrorKey.DefaultErrorMessage]: t(
      'loaEnrichment.factors.sms.inline.addPhoneFailed',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    )
  };

  const inlineTitleAddPhone = inlineTitleMapAddPhone[smsErrorKey]
    ?? t('loaEnrichment.factors.sms.inline.addPhoneFailed', { ns: I18nNamespaces.LOA_ENRICHMENT });

  const inlineTitleMapActivateSMS: Record<string, string> = {
    [AuthErrorKey.DefaultErrorMessage]: t(
      'loaEnrichment.factors.sms.inline.activateFailed',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    ),
    [AuthErrorKey.DefaultSMSChallengeError]: t(
      'loaEnrichment.common.errors.challengeFailedInline',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    )
  };

  const inlineTitleActivateSMS = inlineTitleMapActivateSMS[smsErrorKey]
    ?? t('loaEnrichment.common.errors.challengeFailedInline', { ns: I18nNamespaces.LOA_ENRICHMENT });

  const clearErrors = () => {
    dispatch(setValidationError(''));
    dispatch(setError(''));
  };

  const updateCountryCode = (e: React.ChangeEvent<HTMLInputElement>): void => {
    clearErrors();
    setCountryCode(e.currentTarget.value);
  };

  const updatePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>): void => {
    clearErrors();
    setPhoneNumber(e.currentTarget.value.replace(/[^0-9+ \-.()]|[.\- ()]{2}/g, ''));
  };

  const updateLabel = (e: React.ChangeEvent<HTMLInputElement>): void => {
    clearErrors();
    const normalize = e.target.value;
    if (normalize.length > LABEL_MAX_LENGTH) {
      dispatch(setValidationError(ValErr.LabelTooLong));
    } else if (validationError?.startsWith('label')) {
      dispatch(setValidationError(''));
    }

    setLabel(e.currentTarget.value);
  };

  const sendCode = async () => {
    if (!factorId) return;
    const shouldStartCooldown = await dispatch(challengeFactor(
      t,
      {
        factorId,
        factorType: SecuritySetupType.SMS,
        pageFlow: 'sms-activate',
        errorKey: AuthErrorKey.DefaultSMSChallengeError,
        toastKey: 'loaEnrichment.common.errors.sendFailedToast'
      }
    ) as any);
    if (shouldStartCooldown) {
      setResendIn(LOA_RESEND_SECONDS);
    }
  };

  useEffect(() => {
    if (resendIn <= 0) return undefined;

    const id = setInterval(() => {
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);

    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => () => {
    dispatch(resetEnrollmentState());
  }, [dispatch]);

  const challengedOnceRef = React.useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (step !== 'code') return;
    if (!factorId) return;
    if (challengedOnceRef.current[factorId]) return;

    challengedOnceRef.current[factorId] = true;
    sendCode();
  }, [step, factorId]);

  useEffect(() => {
    if (!smsFactorIdFromUrl) return;

    if (!selectedFactor?.factorId) {
      replaceQueryParams({ smsFactorId: null });
    }
  }, [smsFactorIdFromUrl, selectedFactor?.factorId, replaceQueryParams]);

  const onNext = async (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    clearErrors();

    const normalizedLabel = label.trim();

    if (!normalizedLabel) {
      dispatch(setValidationError(ValErr.LabelRequired));
      return;
    }

    if (normalizedLabel.length > LABEL_MAX_LENGTH) {
      dispatch(setValidationError(ValErr.LabelTooLong));
      return;
    }

    if (FORBIDDEN_CHARACTERS_REGEX.test(normalizedLabel)) {
      dispatch(setValidationError(ValErr.LabelForbiddenChars));
      return;
    }

    if (!parsedPhone || !parsedPhone.isValid()) {
      dispatch(setValidationError(ValErr.SmsInvalidPhone));
      return;
    }

    const newFactorId = await dispatch(enrollSMSFactor(t, {
      label: normalizedLabel,
      countryCode: dialCode,
      nationalNumber,
      loaLevel: requiredLevel ?? 2
    }) as any) as string | undefined;

    if (!newFactorId) return;

    replaceQueryParams({ smsFactorId: newFactorId });
    setCode('');
    setResendIn(0);
  };

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    const input = e.currentTarget.value.replace(/\D/g, '');
    setCode(input.slice(0, digits));
  };

  const submit = (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    if (!factorId) return;

    dispatch(verifyOrActivateFactor(t, {
      factorId,
      code: code.replace(/\s/g, ''),
      action: 'activate',
      successToastMsg: 'loaEnrichment.common.messages.activated',
      elevateLevel: selectedFactor?.loaLevel ?? requiredLevel
    }) as any);
  };

  return (
    <>
      <h1 className={styles.formTitle}>
        {t('loaEnrichment.factors.sms.title')}
      </h1>

      {step === 'details' && (
        <>
          <p className={styles.helpText}>
            {t('loaEnrichment.factors.sms.enrollSubtitle')}
          </p>

          {shouldShowInlineNotification && (
            <div className={styles.inlineNotification}>
              <IlInlineNotification
                title={inlineTitleAddPhone}
                notificationType='inline'
                kind='error'
                hideCloseButton
              />
            </div>
          )}

          <form onSubmit={onNext}>
            <div className={styles.detailsContainer}>
              <IlTextInput
                id='sms-label'
                labelText={t('loaEnrichment.factors.sms.fields.label')}
                placeholder={t('loaEnrichment.common.placeholders.label')}
                type='text'
                onChange={updateLabel}
                invalid={isLabelError}
                invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
                value={label}
                autoFocus
              />

              <IlSelect
                labelText={t('loaEnrichment.factors.sms.fields.countryCode')}
                id='country-code'
                onChange={updateCountryCode}
                value={countryCode}
              >
                {(countries as Country[]).map((country) => (
                  <IlSelectItem
                    key={country.code}
                    value={country.code}
                    text={`${t(country.name, { ns: I18nNamespaces.COUNTRIES_NAMES })} (${country.dial_code})`}
                  />
                ))}
                <IlSelectItem
                  key='Other'
                  value='Other'
                  text={`${t('OTHER', { ns: I18nNamespaces.COUNTRIES_NAMES })}`}
                />
              </IlSelect>

              <IlTextInput
                id='phone-number'
                labelText={t('loaEnrichment.factors.sms.fields.phoneNumber')}
                placeholder={countryCode === 'Other' ? '+1 415 555 2671' : '000-000-0000'}
                type='text'
                onChange={updatePhoneNumber}
                invalid={isPhoneNumberError}
                invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
                value={phoneNumber}
              />
            </div>

            <div className={styles.nextBtnContainer}>
              <IlButton kind='primary' type='submit' disabled={!canNext}>
                {t('loaEnrichment.common.buttons.next')}
              </IlButton>
            </div>
          </form>

          {actionLoading && (
            <div className={styles.loadingContainer}>
              <IlInlineLoading
                description={t('loaEnrichment.common.status.pleaseWait')}
                success={false}
                onSuccess={noop}
              />
            </div>
          )}
        </>
      )}

      {step === 'code' && (
        <>
          <p className={styles.helpText}>
            {t('loaEnrichment.factors.sms.verifyHelpText', {
              phoneNumber: phone || t('loaEnrichment.factors.sms.fallbackPhoneNumber')
            })}
          </p>

          {shouldShowInlineNotification && (
            <div className={styles.inlineNotification}>
              <IlInlineNotification
                title={inlineTitleActivateSMS}
                notificationType='inline'
                kind='error'
                hideCloseButton
              />
            </div>
          )}

          <form onSubmit={submit}>
            <IlTextInput
              id='sms-activation-code'
              labelText={t('loaEnrichment.common.labels.verificationCode')}
              placeholder={t('loaEnrichment.common.placeholders.code', { digits })}
              type='text'
              maxLength={digits}
              invalid={!!validationError}
              invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
              value={code}
              onChange={updateCode}
              autoFocus
              autoComplete='off'
            />

            <div className={styles.activationContainer}>
              <div className={styles.resendContainer}>
                <p className={styles.resendText}>{t('loaEnrichment.common.resend.smsPrompt')}</p>
                <IlButton
                  className={styles.resendBtn}
                  kind='ghost'
                  type='button'
                  disabled={!canResend}
                  onClick={sendCode}
                >
                  {resendIn > 0
                    ? t('loaEnrichment.common.resend.resendIn', { seconds: resendIn })
                    : t('loaEnrichment.common.buttons.resendCode')}
                </IlButton>
              </div>

              <IlButton kind='primary' type='submit' disabled={!canSubmit}>
                {t('loaEnrichment.common.buttons.activate')}
              </IlButton>
            </div>
          </form>

          {actionLoading && (
            <div className={styles.loadingContainer}>
              <IlInlineLoading
                description={t('loaEnrichment.common.status.pleaseWait')}
                success={false}
                onSuccess={noop}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default EnrollSMS;
